(function () {
  /**
   * Contract alignment note:
   * Keep the recommendation payload shape in sync with
   * website/shared/recommendation-contract.md.
   */

  /**
   * @typedef {Object} CompatibleProductShape
   * @property {string} handle
   * @property {string} title
   * @property {number} price
   * @property {number|null} variantId
   * @property {boolean} available
   * @property {string|null} image
   * @property {string} url
   * @property {string} profile
   * @property {string=} type
   * @property {string=} description
   */

  /**
   * @typedef {Object} RecommendationShape
   * @property {"clarity"|"bass"|"cockpit"} goal
   * @property {"Essential"|"Enhanced"|"Signature"} tier
   * @property {string} title
   * @property {string} description
   * @property {CompatibleProductShape[]} items
   */

  function escapeHtml(value) {
    return String(value || "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;");
  }

  function money(amount) {
    if (!amount || Number.isNaN(Number(amount))) return "Rs. 0";
    const value = Number(amount) / 100;
    return `Rs. ${value.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
  }

  function trackEvent(name, payload) {
    const eventName = String(name || "").trim();
    if (!eventName) return;
    const detail = {
      event: eventName,
      ...(payload || {}),
    };

    try {
      window.dispatchEvent(new CustomEvent("stitch:analytics", { detail }));
    } catch (_) {}

    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push(detail);
    }
  }

  function getProfileWeight(goal, profile) {
    const weights = {
      clarity: {
        deck_core: 86,
        deck_plus: 90,
        screen_9: 96,
        screen_10_5: 100,
        bass_8: 42,
        bass_10: 48,
        bass_12: 35,
      },
      bass: {
        deck_core: 38,
        deck_plus: 44,
        screen_9: 54,
        screen_10_5: 62,
        bass_8: 82,
        bass_10: 94,
        bass_12: 100,
      },
      cockpit: {
        deck_core: 76,
        deck_plus: 86,
        screen_9: 95,
        screen_10_5: 100,
        bass_8: 40,
        bass_10: 56,
        bass_12: 60,
      },
    };

    return (weights[goal] && weights[goal][profile]) || 0;
  }

  function getCabinModifier(cabinScale, profile) {
    const modifiers = {
      small: {
        bass_8: 18,
        bass_10: -10,
        bass_12: -45,
        screen_10_5: -6,
      },
      medium: {
        bass_8: 4,
        bass_10: 14,
        bass_12: -8,
        screen_10_5: 2,
      },
      large: {
        bass_8: -10,
        bass_10: 12,
        bass_12: 18,
        screen_10_5: 5,
      },
    };

    return (modifiers[cabinScale] && modifiers[cabinScale][profile]) || 0;
  }

  function uniqueHandles(items) {
    const seen = new Set();
    return items.filter((item) => {
      if (!item || seen.has(item.handle)) return false;
      seen.add(item.handle);
      return true;
    });
  }

  function sumPrice(items) {
    return items.reduce((total, item) => total + (item && item.price ? item.price : 0), 0);
  }

  /**
   * @param {any} product
   * @returns {CompatibleProductShape}
   */
  function normalizeCompatibleProduct(product) {
    return {
      handle: String(product && product.handle ? product.handle : ""),
      title: String(product && product.title ? product.title : "Zessqaa Product"),
      price: Number(product && product.price ? product.price : 0) || 0,
      variantId: product && product.variantId ? Number(product.variantId) : null,
      available: Boolean(product && product.available),
      image: product && product.image ? String(product.image) : null,
      url: String(product && product.url ? product.url : "#"),
      profile: String(product && product.profile ? product.profile : "unknown"),
      type: product && product.type ? String(product.type) : undefined,
      description: product && product.description ? String(product.description) : undefined,
    };
  }

  /**
   * @param {any} recommendation
   * @returns {RecommendationShape}
   */
  function normalizeRecommendation(recommendation) {
    return {
      goal: recommendation.goal,
      tier: recommendation.tier,
      title: recommendation.title,
      description: recommendation.description,
      items: uniqueHandles(recommendation.items).filter(Boolean).map(normalizeCompatibleProduct),
    };
  }

  function createRecommendation(goal, tier, title, description, items) {
    return normalizeRecommendation({
      goal,
      tier,
      title,
      description,
      items,
    });
  }

  function getVehicleArtSpec(slug, segment) {
    const defaults = {
      hatchback: {
        wheelbase: 144,
        height: 58,
        roofStart: 112,
        roofEnd: 214,
        cabinHeight: 38,
        tailLift: 24,
      },
      sedan: {
        wheelbase: 156,
        height: 52,
        roofStart: 104,
        roofEnd: 226,
        cabinHeight: 34,
        tailLift: 12,
      },
      suv_muv: {
        wheelbase: 164,
        height: 66,
        roofStart: 96,
        roofEnd: 236,
        cabinHeight: 42,
        tailLift: 6,
      },
    };

    const overrides = {
      maruti_alto_k10: { wheelbase: 136, height: 56, tailLift: 28 },
      maruti_swift: { wheelbase: 142, height: 56, tailLift: 22 },
      maruti_baleno: { wheelbase: 150, height: 54, tailLift: 18 },
      hyundai_i20: { wheelbase: 148, height: 55, tailLift: 18 },
      tata_altroz: { wheelbase: 149, height: 54, tailLift: 16 },
      honda_city: { wheelbase: 168, height: 50, roofEnd: 232, tailLift: 8 },
      maruti_dzire: { wheelbase: 154, height: 50, roofEnd: 224, tailLift: 10 },
      honda_amaze: { wheelbase: 150, height: 51, roofEnd: 220, tailLift: 10 },
      maruti_brezza: { wheelbase: 160, height: 64, roofEnd: 232 },
      hyundai_creta: { wheelbase: 166, height: 66, roofEnd: 236 },
      hyundai_creta_gen1: { wheelbase: 162, height: 64, roofEnd: 232 },
      hyundai_venue: { wheelbase: 156, height: 63, roofEnd: 228 },
      tata_nexon: { wheelbase: 158, height: 64, roofEnd: 230 },
      tata_nexon_gen1: { wheelbase: 158, height: 64, roofEnd: 230 },
      tata_punch: { wheelbase: 150, height: 62, roofEnd: 222 },
      tata_harrier: { wheelbase: 174, height: 68, roofEnd: 240 },
      kia_seltos: { wheelbase: 168, height: 66, roofEnd: 236 },
      kia_seltos_gen1: { wheelbase: 165, height: 65, roofEnd: 234 },
      kia_sonet: { wheelbase: 154, height: 63, roofEnd: 228 },
      toyota_innova_crysta: { wheelbase: 182, height: 70, roofStart: 90, roofEnd: 244 },
      toyota_fortuner: { wheelbase: 176, height: 70, roofStart: 92, roofEnd: 240 },
      mahindra_scorpio: { wheelbase: 178, height: 71, roofStart: 88, roofEnd: 242 },
      mahindra_thar: { wheelbase: 150, height: 67, roofStart: 102, roofEnd: 216, tailLift: 2 },
      mahindra_xuv700: { wheelbase: 172, height: 68, roofStart: 92, roofEnd: 238 },
      mg_hector: { wheelbase: 170, height: 67, roofStart: 92, roofEnd: 238 },
      maruti_ertiga: { wheelbase: 176, height: 69, roofStart: 92, roofEnd: 238 },
      maruti_wagonr: { wheelbase: 150, height: 63, roofStart: 106, roofEnd: 214, cabinHeight: 44, tailLift: 20 },
      hyundai_grand_i10: { wheelbase: 142, height: 56, roofStart: 112, roofEnd: 214, tailLift: 20 },
    };

    return { ...(defaults[segment] || defaults.hatchback), ...(overrides[slug] || {}) };
  }

  function renderVehicleArtwork(car, variant) {
    if (car.image) {
      const label = escapeHtml(car.label);
      const meta = escapeHtml(car.notes || "");
      const cardClass = variant === "summary" ? "is-summary" : "is-card";

      return `
        <div class="stitch-vehicle-art ${cardClass}" aria-hidden="true">
          <img src="${escapeHtml(car.image)}" alt="${label}" loading="lazy">
          ${
            variant === "summary"
              ? `<div class="stitch-vehicle-art__caption"><strong>${label}</strong><span>${meta}</span></div>`
              : ""
          }
        </div>
      `;
    }

    const spec = getVehicleArtSpec(car.slug, car.segment);
    const width = 320;
    const height = 180;
    const frontWheel = 92;
    const rearWheel = Math.min(frontWheel + spec.wheelbase, 254);
    const baseY = 118;
    const roofStart = spec.roofStart;
    const roofEnd = spec.roofEnd;
    const roofY = baseY - spec.height;
    const cabinY = roofY + spec.cabinHeight;
    const tailY = baseY - spec.tailLift;
    const palette =
      car.segment === "suv_muv"
        ? { backgroundA: "#26211c", backgroundB: "#0f0f10", accent: "#f0d79a" }
        : car.segment === "sedan"
          ? { backgroundA: "#201d18", backgroundB: "#111112", accent: "#ead09a" }
          : { backgroundA: "#231f1a", backgroundB: "#111111", accent: "#ebc77f" };

    const cardClass = variant === "summary" ? "is-summary" : "is-card";
    const label = escapeHtml(car.label);
    const meta = escapeHtml(car.notes || "");

    return `
      <div class="stitch-vehicle-art ${cardClass}" aria-hidden="true">
        <svg viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg" role="presentation">
          <defs>
            <linearGradient id="vehicleBg-${car.slug}" x1="24" y1="24" x2="286" y2="170" gradientUnits="userSpaceOnUse">
              <stop stop-color="${palette.backgroundA}" />
              <stop offset="1" stop-color="${palette.backgroundB}" />
            </linearGradient>
            <linearGradient id="vehicleAccent-${car.slug}" x1="${roofStart}" y1="${roofY}" x2="${roofEnd}" y2="${baseY}" gradientUnits="userSpaceOnUse">
              <stop stop-color="${palette.accent}" />
              <stop offset="1" stop-color="#b7903f" />
            </linearGradient>
          </defs>
          <rect x="0" y="0" width="${width}" height="${height}" rx="26" fill="url(#vehicleBg-${car.slug})" />
          <path d="M44 ${baseY + 2}C48 ${baseY - 16} 61 ${baseY - 30} 84 ${baseY - 36}L${roofStart} ${roofY + 2}H${roofEnd - 26}C${roofEnd - 12} ${roofY + 2} ${roofEnd} ${roofY + 10} ${roofEnd + 10} ${roofY + 18}L${rearWheel + 26} ${baseY - 8}H274C282 ${baseY - 8} 288 ${baseY - 2} 288 ${baseY + 8}V${baseY + 14}H274C270 ${baseY + 28} 259 ${baseY + 38} 245 ${baseY + 38}C231 ${baseY + 38} 220 ${baseY + 28} 216 ${baseY + 14}H123C119 ${baseY + 28} 108 ${baseY + 38} 94 ${baseY + 38}C80 ${baseY + 38} 69 ${baseY + 28} 65 ${baseY + 14}H44V${baseY + 10}C44 ${baseY + 6} 44 ${baseY + 4} 44 ${baseY + 2}Z" fill="rgba(255,255,255,0.92)" />
          <path d="M${roofStart + 4} ${roofY + 4}H${roofEnd - 28}C${roofEnd - 18} ${roofY + 4} ${roofEnd - 8} ${roofY + 10} ${roofEnd + 2} ${roofY + 20}L${rearWheel + 12} ${baseY - 12}H${roofStart - 14}L${roofStart - 22} ${roofY + 22}C${roofStart - 24} ${roofY + 14} ${roofStart - 18} ${roofY + 6} ${roofStart - 8} ${roofY + 4}Z" fill="url(#vehicleAccent-${car.slug})" fill-opacity="0.92" />
          <path d="M${roofStart + 14} ${roofY + 8}H${roofEnd - 40}" stroke="rgba(255,255,255,0.4)" stroke-width="2" stroke-linecap="round" />
          <circle cx="${frontWheel}" cy="${baseY + 13}" r="17" fill="#161310" />
          <circle cx="${frontWheel}" cy="${baseY + 13}" r="8" fill="#d1bc8f" />
          <circle cx="${rearWheel}" cy="${baseY + 13}" r="17" fill="#161310" />
          <circle cx="${rearWheel}" cy="${baseY + 13}" r="8" fill="#d1bc8f" />
        </svg>
        ${
          variant === "summary"
            ? `<div class="stitch-vehicle-art__caption"><strong>${label}</strong><span>${meta}</span></div>`
            : ""
        }
      </div>
    `;
  }

  function chooseFirst(products, profiles) {
    return products.find((product) => profiles.includes(product.profile));
  }

  function buildRecommendations(goal, carProducts, car) {
    const byProfile = (profiles) => chooseFirst(carProducts, profiles);

    const coreDeck = byProfile(["deck_core", "deck_plus"]);
    const premiumDeck = byProfile(["screen_9", "screen_10_5", "deck_plus"]);
    const heroBass = byProfile(
      car.cabinScale === "large"
        ? ["bass_12", "bass_10", "bass_8"]
        : car.cabinScale === "medium"
          ? ["bass_10", "bass_8", "bass_12"]
          : ["bass_8", "bass_10", "bass_12"]
    );
    const statementBass = byProfile(["bass_12", "bass_10", "bass_8"]);
    const signatureScreen = byProfile(["screen_10_5", "screen_9", "deck_plus", "deck_core"]);

    if (goal === "bass") {
      return [
        createRecommendation(
          goal,
          "Essential",
          "Essential Bass Upgrade",
          "A lower-friction bass step-up built around the most sensible low-end option for this cabin.",
          [heroBass, coreDeck]
        ),
        createRecommendation(
          goal,
          "Enhanced",
          "Enhanced Bass Upgrade",
          "Adds a smarter source and a stronger bass foundation for a more complete daily setup.",
          [heroBass || statementBass, premiumDeck]
        ),
        createRecommendation(
          goal,
          "Signature",
          "Signature Bass Upgrade",
          "The highest-authority combination this car can realistically carry without guesswork.",
          [statementBass || heroBass, signatureScreen]
        ),
      ];
    }

    if (goal === "cockpit") {
      return [
        createRecommendation(
          goal,
          "Essential",
          "Essential Cockpit Upgrade",
          "The quickest route to a more modern in-car interface without overbuilding the setup.",
          [premiumDeck || coreDeck]
        ),
        createRecommendation(
          goal,
          "Enhanced",
          "Enhanced Cockpit Upgrade",
          "A more complete daily-driver package with a smarter screen and a stronger audio anchor.",
          [premiumDeck || coreDeck, heroBass]
        ),
        createRecommendation(
          goal,
          "Signature",
          "Signature Cockpit Upgrade",
          "The premium dashboard-led recommendation for visitors who want the cabin to feel transformed.",
          [signatureScreen || premiumDeck || coreDeck, statementBass || heroBass]
        ),
      ];
    }

    return [
      createRecommendation(
        goal,
        "Essential",
        "Essential Clarity Upgrade",
        "A cleaner everyday setup for visitors who want a more composed cabin without chasing maximum bass.",
        [coreDeck || premiumDeck]
      ),
      createRecommendation(
        goal,
        "Enhanced",
        "Enhanced Clarity Upgrade",
        "Balances a better source unit with a fuller but still controlled low-end presence.",
        [premiumDeck || coreDeck, heroBass]
      ),
      createRecommendation(
        goal,
        "Signature",
        "Signature Clarity Upgrade",
        "A more premium cockpit-plus-bass combination for a richer and more polished long-drive feel.",
        [signatureScreen || premiumDeck || coreDeck, heroBass]
      ),
    ];
  }

  function renderRecommendationCard(recommendation) {
    const goalLabels = {
      clarity: "Everyday Clarity",
      bass: "Bass Upgrade",
      cockpit: "Connected Cockpit",
    };
    const included = recommendation.items
      .map((item) => `<li>${item.title}</li>`)
      .join("");

    return `
      <article class="stitch-recommendation-card" data-tier="${recommendation.tier}">
        <div class="stitch-recommendation-card__topline">
          <span class="stitch-recommendation-card__tier">${recommendation.tier}</span>
          <span class="stitch-recommendation-card__goal">${goalLabels[recommendation.goal] || recommendation.goal}</span>
        </div>
        <h3 class="stitch-title-md">${recommendation.title}</h3>
        <p class="stitch-copy-muted">${recommendation.description}</p>
        <ul class="stitch-recommendation-card__list" role="list">${included}</ul>
        <div class="stitch-recommendation-card__bottom">
          <span class="stitch-recommendation-card__price">From ${money(sumPrice(recommendation.items))}</span>
          <button type="button" class="stitch-button stitch-button--ghost" data-view-setup="${recommendation.tier}">
            View Setup
          </button>
        </div>
      </article>
    `;
  }

  function renderCompatibleCard(product, highlighted) {
    const image = product.image
      ? `<img src="${product.image}" alt="${product.title}">`
      : `<div class="stitch-compatible-card__placeholder"></div>`;
    const controls = product.available && product.variantId
      ? `
        <div
          class="stitch-cart-controls"
          data-cart-product
          data-variant-id="${product.variantId}"
          data-product-url="${product.url}"
        >
          <button type="button" class="stitch-button stitch-button--ghost" data-cart-add>Add to cart</button>
        </div>
      `
      : `<a href="${product.url}" class="stitch-button stitch-button--ghost">View product</a>`;

    return `
      <article
        class="stitch-compatible-card${highlighted ? " is-highlighted" : ""}"
        data-compatible-handle="${product.handle}"
        data-product-card
        data-product-url="${product.url}"
      >
        <a href="${product.url}" class="stitch-compatible-card__media">${image}</a>
        <div class="stitch-compatible-card__body">
          <span class="stitch-kicker">${product.type || "Zessqaa"}</span>
          <h3 class="stitch-title-md"><a href="${product.url}">${product.title}</a></h3>
          <p class="stitch-copy-muted">${product.description || ""}</p>
          <div class="stitch-compatible-card__bottom">
            <span class="stitch-price">${money(product.price)}</span>
            ${controls}
          </div>
        </div>
      </article>
    `;
  }

  function renderCarOption(car, active) {
    return `
      <button
        type="button"
        class="stitch-car-option${active ? " is-active" : ""}"
        data-car-option="${escapeHtml(car.slug)}"
        aria-pressed="${active ? "true" : "false"}"
      >
        ${renderVehicleArtwork(car, "card")}
        <span class="stitch-car-option__body">
          <span class="stitch-car-option__label">${escapeHtml(car.label)}</span>
          <span class="stitch-car-option__meta">${escapeHtml(car.notes || "")}</span>
        </span>
      </button>
    `;
  }

  const cartStore = {
    cart: null,
  };

  async function requestJson(url, options) {
    const response = await fetch(url, {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      ...options,
    });

    if (!response.ok) {
      throw new Error(`Cart request failed: ${response.status}`);
    }

    return response.json();
  }

  async function fetchCart(force) {
    if (!force && cartStore.cart) return cartStore.cart;
    cartStore.cart = await requestJson("/cart.js", { method: "GET" });
    return cartStore.cart;
  }

  function syncCartCountBadge(cart) {
    const cartLink = document.querySelector(".stitch-header__actions a[aria-label='Cart']");
    if (!cartLink) return;

    const count = Number(cart && cart.item_count ? cart.item_count : 0);
    let badge = cartLink.querySelector(".stitch-count");
    if (count > 0) {
      if (!badge) {
        badge = document.createElement("span");
        badge.className = "stitch-count";
        cartLink.appendChild(badge);
      }
      badge.textContent = String(count);
    } else if (badge) {
      badge.remove();
    }
  }

  function adjustCartCountBadge(delta) {
    const cartLink = document.querySelector(".stitch-header__actions a[aria-label='Cart']");
    if (!cartLink) return;

    let badge = cartLink.querySelector(".stitch-count");
    const current = badge ? Number(badge.textContent || 0) : 0;
    const next = Math.max(0, current + Number(delta || 0));

    if (next > 0) {
      if (!badge) {
        badge = document.createElement("span");
        badge.className = "stitch-count";
        cartLink.appendChild(badge);
      }
      badge.textContent = String(next);
      badge.classList.remove('stitch-count--bounce');
      void badge.offsetWidth;
      badge.classList.add('stitch-count--bounce');
    } else if (badge) {
      badge.remove();
    }
  }

  async function refreshCart(force) {
    const cart = await fetchCart(force);
    syncCartCountBadge(cart);
    return cart;
  }

  async function addVariantToCart(variantId, quantity) {
    trackEvent("selector_atc_intent", {
      variant_id: Number(variantId || 0),
      quantity: Number(quantity || 1),
    });
    if (window._learnq) {
      window._learnq.push(["track", "Added to Cart", {
        "variant_id": Number(variantId || 0),
        "quantity": Number(quantity || 1)
      }]);
    }
    await requestJson("/cart/add.js", {
      method: "POST",
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: Number(quantity || 1) }],
      }),
    });
    if (window.fbq) { fbq('track', 'AddToCart', { currency: 'INR', event_id: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : String(Date.now()) }); }
    const cart = await refreshCart(true).catch(function() { return null; });
    return cart;
  }

  async function addVariantsBundle(variantIds) {
    const items = Array.from(new Set((variantIds || []).map((id) => Number(id)).filter(Boolean))).map((id) => ({
      id,
      quantity: 1,
    }));
    if (!items.length) return fetchCart(false);
    trackEvent("selector_bundle_add", {
      bundle_variant_ids: items.map((item) => item.id),
    });

    await requestJson("/cart/add.js", {
      method: "POST",
      body: JSON.stringify({ items }),
    });
    refreshCart(true).catch(() => {});
  }

  function animateAddedState(button, label) {
    if (!button) return;
    const original = button.dataset.originalLabel || button.textContent || "Add to cart";
    button.dataset.originalLabel = original;
    button.textContent = label || "Added";
    button.classList.add("is-added");
    window.clearTimeout(Number(button.dataset.addedTimeout || 0));
    const timeoutId = window.setTimeout(() => {
      button.classList.remove("is-added");
      button.textContent = original;
      button.dataset.addedTimeout = "";
    }, 1200);
    button.dataset.addedTimeout = String(timeoutId);
  }

  function initCardNavigationAndCart(scope) {
    scope.addEventListener("click", async (event) => {
      const interactive = event.target.closest("a, button, input, select, textarea, label");
      const card = event.target.closest("[data-product-card]");

      if (card && !interactive) {
        const url = card.dataset.productUrl;
        if (url) {
          window.location.href = url;
        }
        return;
      }

      const cartControl = event.target.closest("[data-cart-product]");
      if (!cartControl) return;

      const variantId = Number(cartControl.dataset.variantId || 0);
      if (!variantId) return;

      const addButton = event.target.closest("[data-cart-add]");
      if (addButton) {
        event.preventDefault();
        animateAddedState(addButton, "Added");
        adjustCartCountBadge(1);
        trackEvent("pdp_or_grid_atc_click", {
          variant_id: variantId,
          source: card ? "card" : "inline",
        });
        addVariantToCart(variantId, 1).catch(() => {
          refreshCart(true).catch(() => {});
        });
        return;
      }
    });
  }

  function formatStorefrontMoney(value) {
    if (value === null || value === undefined || value === "") return "₹0";

    const numeric = Number(value);
    if (Number.isNaN(numeric)) return "₹0";

    const normalized = numeric > 9999 ? numeric / 100 : numeric;
    return "₹" + normalized.toLocaleString("en-IN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: normalized % 1 === 0 ? 0 : 2,
    });
  }

  function initPdpGalleryAndVariants() {
    const heroImage = document.querySelector(".stitch-product-gallery__hero img");
    const thumbs = Array.from(document.querySelectorAll(".stitch-product-gallery__thumb"));
    const swatches = Array.from(document.querySelectorAll(".stitch-swatch input[type='radio'][name='id']"));
    const panelPrice = document.querySelector(".stitch-product-panel > .stitch-price");
    const stickyPrice = document.querySelector(".stitch-pdp-sticky .stitch-price");
    const stickyVariantInput = document.querySelector(".stitch-pdp-sticky__form input[name='id']");
    const mobileStickyVariantInput = document.querySelector(".stitch-pdp-mobile-sticky form input[name='id']");

    if (thumbs.length && heroImage) {
      const activateThumb = (thumb) => {
        thumbs.forEach((item) => item.classList.remove("is-active"));
        thumb.classList.add("is-active");
      };

      activateThumb(thumbs[0]);

      thumbs.forEach((thumb) => {
        thumb.addEventListener("click", () => {
          const thumbImage = thumb.querySelector("img");
          if (!thumbImage) return;

          activateThumb(thumb);
          heroImage.src = thumbImage.src;
          heroImage.srcset = thumbImage.srcset || "";
          heroImage.alt = thumbImage.alt || heroImage.alt;
        });
      });
    }

    if (!swatches.length) return;

    const swatchControls = Array.from(document.querySelectorAll(".stitch-swatch"));
    const updateVariantUi = (input) => {
      if (!input) return;
      swatchControls.forEach((control) => control.classList.remove("is-active"));
      const parent = input.closest(".stitch-swatch");
      if (parent) parent.classList.add("is-active");

      const variantId = input.value;
      if (stickyVariantInput) stickyVariantInput.value = variantId;
      if (mobileStickyVariantInput) mobileStickyVariantInput.value = variantId;

      const variantPrice = Number(input.dataset.variantPrice || 0);
      if (variantPrice > 0) {
        const formatted = formatStorefrontMoney(variantPrice);
        if (panelPrice) panelPrice.textContent = formatted;
        if (stickyPrice) stickyPrice.textContent = formatted;
      }
    };

    swatches.forEach((input) => {
      input.addEventListener("change", () => {
        if (input.checked) updateVariantUi(input);
      });
    });

    const checked = swatches.find((input) => input.checked) || swatches[0];
    if (checked) updateVariantUi(checked);
  }

  function initPdpFormValidation() {
    const form = document.querySelector("[data-pdp-form]");
    const quantityInput = form && form.querySelector("#Quantity");
    if (!form || !quantityInput) return;

    const setError = (message) => {
      quantityInput.setAttribute("aria-invalid", "true");
      let error = form.querySelector("[data-quantity-error]");
      if (!error) {
        error = document.createElement("p");
        error.className = "stitch-form-error";
        error.dataset.quantityError = "true";
        quantityInput.insertAdjacentElement("afterend", error);
      }
      error.textContent = message;
    };

    const clearError = () => {
      quantityInput.removeAttribute("aria-invalid");
      const error = form.querySelector("[data-quantity-error]");
      if (error) error.remove();
    };

    quantityInput.addEventListener("input", clearError);

    form.addEventListener("submit", (event) => {
      const rawValue = String(quantityInput.value || "").trim();
      const quantity = Number(rawValue);
      const isInteger = Number.isInteger(quantity);
      if (!rawValue || !isInteger || quantity < 1) {
        event.preventDefault();
        setError("Enter valid quantity (minimum 1).");
        return;
      }
      clearError();
    });
  }

  function initRevealUp() {
    const reveals = document.querySelectorAll(".stitch-reveal-up");
    if (!reveals.length) return;

    if (typeof IntersectionObserver === "undefined") {
      reveals.forEach((el) => el.classList.add("is-visible"));
      return;
    }

    const revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            revealObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    reveals.forEach((el) => revealObserver.observe(el));
  }

  function initMobileNavDrawer() {
    const toggle = document.querySelector("[data-nav-toggle]");
    const drawer = document.querySelector("[data-nav-drawer]");
    const overlay = document.querySelector("[data-nav-overlay]");
    const closeButton = document.querySelector("[data-nav-close]");
    const icon = document.querySelector("[data-nav-icon]");

    if (!toggle || !drawer) return;

    function openDrawer() {
      drawer.classList.add("is-open");
      drawer.setAttribute("aria-hidden", "false");
      toggle.setAttribute("aria-expanded", "true");
      if (icon) icon.textContent = "close";
      document.body.style.overflow = "hidden";
    }

    function closeDrawer() {
      drawer.classList.remove("is-open");
      drawer.setAttribute("aria-hidden", "true");
      toggle.setAttribute("aria-expanded", "false");
      if (icon) icon.textContent = "menu";
      document.body.style.overflow = "";
    }

    toggle.addEventListener("click", function () {
      if (drawer.classList.contains("is-open")) {
        closeDrawer();
      } else {
        openDrawer();
      }
    });

    if (overlay) overlay.addEventListener("click", closeDrawer);
    if (closeButton) closeButton.addEventListener("click", closeDrawer);

    document.addEventListener("keydown", function (event) {
      if (event.key === "Escape" && drawer.classList.contains("is-open")) {
        closeDrawer();
      }
    });
  }

  function initInstantSearchOverlay() {
    const searchToggle = document.querySelector("[data-search-toggle]");
    const searchOverlay = document.querySelector("[data-search-overlay]");
    const searchBackdrop = document.querySelector("[data-search-backdrop]");
    const searchInput = document.querySelector("[data-search-input]");
    const searchClose = document.querySelector("[data-search-close]");
    const searchResults = document.querySelector("[data-search-results]");

    if (!searchToggle || !searchOverlay) return;

    let allProducts = null;
    let productRequest = null;

    function fetchProducts() {
      if (allProducts) return Promise.resolve(allProducts);
      if (productRequest) return productRequest;

      productRequest = fetch("/collections/all/products.json?limit=250")
        .then(function (response) {
          return response.json();
        })
        .then(function (payload) {
          allProducts = payload.products || [];
          return allProducts;
        })
        .catch(function () {
          allProducts = [];
          return allProducts;
        });

      return productRequest;
    }

    function openSearch() {
      searchOverlay.classList.add("is-open");
      searchOverlay.setAttribute("aria-hidden", "false");
      document.body.style.overflow = "hidden";
      if (searchInput) {
        window.setTimeout(function () {
          searchInput.focus();
        }, 100);
      }
      if (!allProducts) fetchProducts().then(function () {
        if (searchInput && searchInput.value.trim()) {
          renderResults(searchInput.value.trim());
        }
      });
    }

    function closeSearch() {
      searchOverlay.classList.remove("is-open");
      searchOverlay.setAttribute("aria-hidden", "true");
      document.body.style.overflow = "";
      if (searchInput) searchInput.value = "";
      if (searchResults) searchResults.innerHTML = "";
    }

    function renderResults(query) {
      if (!searchResults) return;

      const trimmed = String(query || "").trim();
      if (trimmed.length < 2) {
        searchResults.innerHTML = "";
        return;
      }

      if (!allProducts) {
        searchResults.innerHTML = '<p class="stitch-search-empty">Loading...</p>';
        return;
      }

      const normalizedQuery = trimmed.toLowerCase();
      const allMatches = allProducts
        .filter(function (product) {
          return (
            String(product.title || "").toLowerCase().includes(normalizedQuery) ||
            String(product.product_type || "").toLowerCase().includes(normalizedQuery) ||
            String(product.vendor || "").toLowerCase().includes(normalizedQuery) ||
            String((product.tags || []).join(" ")).toLowerCase().includes(normalizedQuery)
          );
        });
      const matches = allMatches.slice(0, 6);

      if (!matches.length) {
        searchResults.innerHTML = '<p class="stitch-search-empty">No products found for "' + escapeHtml(trimmed) + '"</p>';
        return;
      }

      var resultsHtml = matches
        .map(function (product) {
          const image = product.images && product.images[0]
            ? '<img class="stitch-search-result__thumb" src="' + escapeHtml(product.images[0].src) + '" alt="' + escapeHtml(product.title) + '" loading="lazy">'
            : '<div class="stitch-search-result__thumb"></div>';
          const variant = product.variants && product.variants[0] ? product.variants[0] : null;
          const available = !variant || variant.available;
          const availBadge = available ? '' : '<span class="stitch-search-result__oos">Out of stock</span>';

          return (
            '<a class="stitch-search-result" href="/products/' + escapeHtml(product.handle) + '">' +
              image +
              '<div class="stitch-search-result__info">' +
                '<span class="stitch-search-result__title">' + escapeHtml(product.title) + "</span>" +
                '<span class="stitch-search-result__price">' + formatStorefrontMoney(variant ? variant.price : product.price) + availBadge + "</span>" +
              "</div>" +
            "</a>"
          );
        })
        .join("");

      if (allMatches.length > 6) {
        resultsHtml += '<a class="stitch-search-view-all" href="/search?q=' + encodeURIComponent(trimmed) + '">View all ' + allMatches.length + ' results for &ldquo;' + escapeHtml(trimmed) + '&rdquo; &rarr;</a>';
      }

      searchResults.innerHTML = resultsHtml;
    }

    searchToggle.addEventListener("click", openSearch);
    if (searchBackdrop) searchBackdrop.addEventListener("click", closeSearch);
    if (searchClose) searchClose.addEventListener("click", closeSearch);

    var searchTimer = null;
    if (searchInput) {
      searchInput.addEventListener("input", function () {
        var val = this.value;
        clearTimeout(searchTimer);
        searchTimer = setTimeout(function () { renderResults(val); }, 220);
      });
    }

    document.addEventListener("keydown", function (event) {
      if (event.key === "Escape" && searchOverlay.classList.contains("is-open")) {
        closeSearch();
      }
    });
  }

  function initPdpStickyBar() {
    const stickyBar = document.querySelector("[data-pdp-sticky]");
    const mainActions = document.querySelector(".stitch-product-actions");
    if (!stickyBar || !mainActions || typeof IntersectionObserver === "undefined") return;

    function toPixels(value) {
      const parsed = Number.parseFloat(String(value || "").trim());
      return Number.isFinite(parsed) ? parsed : 0;
    }

    function getStickyTriggerOffset() {
      const rootStyle = window.getComputedStyle(document.documentElement);
      const cssOffset = toPixels(rootStyle.getPropertyValue("--stitch-top-stack-height"));
      if (cssOffset > 0) return cssOffset;

      const header = document.querySelector(".stitch-header");
      const announce = document.querySelector(".stitch-announce-bar");
      const headerHeight = header ? header.getBoundingClientRect().height : 0;
      const announceHeight = announce ? announce.getBoundingClientRect().height : 0;
      return headerHeight + announceHeight;
    }

    function toggleSticky(isVisible) {
      stickyBar.classList.toggle("is-visible", isVisible);
      stickyBar.setAttribute("aria-hidden", isVisible ? "false" : "true");
    }

    let observer = null;
    let rafId = null;

    function bindObserver() {
      const offset = Math.round(getStickyTriggerOffset());
      const rootMargin = `-${offset}px 0px 0px 0px`;

      if (observer) observer.disconnect();
      observer = new IntersectionObserver(
        function (entries) {
          const entry = entries[0];
          if (!entry) return;
          toggleSticky(!entry.isIntersecting);
        },
        { threshold: 0, rootMargin: rootMargin }
      );
      observer.observe(mainActions);
    }

    function rebindObserver() {
      if (rafId) window.cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(bindObserver);
    }

    bindObserver();
    window.addEventListener("resize", rebindObserver, { passive: true });
  }

  function initVehicleMatch(root) {
    const scope = root.closest(".stitch-page") || root.parentElement || document;
    const dataNode =
      root.querySelector("[data-zessqaa-recommendation-data]") ||
      scope.querySelector("[data-zessqaa-recommendation-data]");
    if (!dataNode) return;

    const data = JSON.parse(dataNode.textContent);
    const params = new URLSearchParams(window.location.search);
    const productsByHandle = data.products.reduce((accumulator, product) => {
      if (product && product.handle) accumulator[product.handle] = product;
      return accumulator;
    }, {});

    const segmentSelector = root.querySelector("[data-segment-selector]");
    const carPanel = root.querySelector("[data-car-panel]");
    const carGrid = root.querySelector("[data-car-grid]");
    const carSearch = root.querySelector("[data-car-search]");
    const carSearchSubmit = root.querySelector("[data-car-search-submit]");
    const goalSelector = root.querySelector("[data-goal-selector]");
    const makeSelect = root.querySelector("[data-make-select]");
    const modelSelect = root.querySelector("[data-model-select]");
    const yearSelect = root.querySelector("[data-year-select]");
    const status = root.querySelector("[data-vehicle-status]");
    const results = root.querySelector("[data-vehicle-results]");
    const recommendationHeading = root.querySelector("[data-recommendation-heading]");
    const recommendationCopy = root.querySelector("[data-recommendation-copy]");
    const compatibleHeading = root.querySelector("[data-compatible-heading]");
    const recommendationGrid = root.querySelector("[data-recommendation-grid]");
    const compatibleGrid = root.querySelector("[data-compatible-grid]");
    const bundleCta = root.querySelector("[data-bundle-cta]");
    const selectedVehicle = root.querySelector("[data-selected-vehicle]");
    const resultsContext = root.querySelector("[data-results-context]");

    let activeSegment = params.get("segment") || root.dataset.defaultSegment || "";
    let activeGoal = params.get("goal") || root.dataset.defaultGoal || "clarity";
    let activeCar = params.get("car") || root.dataset.defaultCar || "";
    let highlightedHandles = [];
    let lastRecommendations = [];
    let selectedBundleVariantIds = [];

    if (!data.soundGoals.some((goal) => goal.id === activeGoal)) {
      activeGoal = "clarity";
    }

    function getCarsForSegment(segment) {
      return data.cars.filter((car) => car.segment === segment);
    }

    function filterCars(cars, query) {
      const normalized = String(query || "").trim().toLowerCase();
      if (!normalized) return cars;

      return cars.filter((car) => {
        const label = String(car.label || "").toLowerCase();
        const slug = String(car.slug || "").replaceAll("_", " ").toLowerCase();
        return label.includes(normalized) || slug.includes(normalized);
      });
    }

    function getRankedProducts(car) {
      const baseProducts = car.compatibleProducts
        .map((handle) => productsByHandle[handle])
        .filter(Boolean);

      const ledFitmentKey = data.ledFitment && data.ledFitment[car.slug];
      const ledProduct = ledFitmentKey && data.ledProducts ? data.ledProducts[ledFitmentKey] : null;
      const merged = ledProduct ? baseProducts.concat(ledProduct) : baseProducts;

      return merged
        .map((product) => {
          const normalized = normalizeCompatibleProduct(product);
          return {
            ...normalized,
            score: getProfileWeight(activeGoal, normalized.profile) + getCabinModifier(car.cabinScale, normalized.profile),
          };
        })
        .sort((a, b) => b.score - a.score);
    }

    function updateSegmentUI() {
      root.querySelectorAll("[data-segment-option]").forEach((button) => {
        const isActive = button.dataset.segmentOption === activeSegment;
        button.classList.toggle("is-active", isActive);
        button.setAttribute("aria-pressed", isActive ? "true" : "false");
      });
    }

    function updateGoalUI() {
      root.querySelectorAll("[data-goal-option]").forEach((button) => {
        const isActive = button.dataset.goalOption === activeGoal;
        button.classList.toggle("is-active", isActive);
        button.setAttribute("aria-pressed", isActive ? "true" : "false");
      });
    }

    function formatSegmentLabel(segment) {
      if (!segment) return "";

      const labels = {
        hatchback: "Hatchback",
        sedan: "Sedan",
        suv_muv: "SUV / MUV",
      };

      return labels[segment] || String(segment).replaceAll("_", " ");
    }

    const MAKE_FROM_SLUG = {
      maruti: "Maruti Suzuki",
      hyundai: "Hyundai",
      tata: "Tata",
      honda: "Honda",
      kia: "Kia",
      toyota: "Toyota",
      mahindra: "Mahindra",
      mg: "MG",
      renault: "Renault",
      nissan: "Nissan",
      volkswagen: "Volkswagen",
      skoda: "Skoda",
      jeep: "Jeep",
      ford: "Ford",
      citroen: "Citroen",
      datsun: "Datsun",
    };

    function getMakeFromSlug(slug) {
      const prefix = (slug || "").split("_")[0].toLowerCase();
      return MAKE_FROM_SLUG[prefix] || (prefix.charAt(0).toUpperCase() + prefix.slice(1));
    }

    function initMakeModelSelects() {
      if (!makeSelect || !modelSelect) return;

      const seenMakes = new Set();
      data.cars.forEach((car) => {
        const make = getMakeFromSlug(car.slug);
        if (!seenMakes.has(make)) {
          seenMakes.add(make);
          const opt = document.createElement("option");
          opt.value = make;
          opt.textContent = make;
          makeSelect.appendChild(opt);
        }
      });

      makeSelect.addEventListener("change", () => {
        const selectedMake = makeSelect.value;
        modelSelect.innerHTML = '<option value="">Select model...</option>';
        modelSelect.disabled = true;
        if (yearSelect) {
          yearSelect.innerHTML = '<option value="">Select year...</option>';
          yearSelect.disabled = true;
        }
        activeCar = "";
        highlightedHandles = [];
        if (!selectedMake) {
          renderResults();
          return;
        }
        const seenModels = new Set();
        data.cars
          .filter((car) => getMakeFromSlug(car.slug) === selectedMake)
          .forEach((car) => {
            const modelName = car.label.replace(selectedMake + " ", "").trim();
            if (!seenModels.has(modelName)) {
              seenModels.add(modelName);
              const opt = document.createElement("option");
              opt.value = modelName;
              opt.textContent = modelName;
              modelSelect.appendChild(opt);
            }
          });
        modelSelect.disabled = false;
        renderResults();
      });

      modelSelect.addEventListener("change", () => {
        const selectedMake = makeSelect.value;
        const selectedModel = modelSelect.value;
        if (yearSelect) {
          yearSelect.innerHTML = '<option value="">Select year...</option>';
          yearSelect.disabled = true;
        }
        activeCar = "";
        highlightedHandles = [];
        if (!selectedModel) {
          renderResults();
          return;
        }
        const matchingCars = data.cars.filter(
          (car) =>
            getMakeFromSlug(car.slug) === selectedMake &&
            car.label.replace(selectedMake + " ", "").trim() === selectedModel
        );
        if (matchingCars.length === 1) {
          activeCar = matchingCars[0].slug;
          if (yearSelect) {
            const opt = document.createElement("option");
            opt.value = matchingCars[0].slug;
            opt.textContent = matchingCars[0].yearRange || "All years";
            yearSelect.appendChild(opt);
            yearSelect.value = matchingCars[0].slug;
            yearSelect.disabled = false;
          }
          const car = matchingCars[0];
          activeSegment = car.segment;
          syncSelectedCarState(car);
          trackEvent("vehicle_selector_car", { car: car.slug, segment: car.segment });
        } else if (yearSelect) {
          matchingCars.forEach((car) => {
            const opt = document.createElement("option");
            opt.value = car.slug;
            opt.textContent = car.yearRange || car.slug;
            yearSelect.appendChild(opt);
          });
          yearSelect.disabled = false;
        }
        renderResults();
      });

      if (yearSelect) {
        yearSelect.addEventListener("change", () => {
          activeCar = yearSelect.value || "";
          highlightedHandles = [];
          if (activeCar) {
            const car = data.cars.find((c) => c.slug === activeCar);
            if (car) {
              activeSegment = car.segment;
              syncSelectedCarState(car);
              trackEvent("vehicle_selector_car", { car: car.slug, segment: car.segment });
            }
          }
          renderResults();
        });
      }
    }

    function syncSelectedCarState(car) {
      if (!car) {
        window._stitchSelectedCar = "";
        window._stitchSelectedSegment = "";
        return;
      }

      window._stitchSelectedCar = car.name || car.label || "";
      window._stitchSelectedSegment = formatSegmentLabel(activeSegment || car.segment || "");
    }

    function renderCars() {
      if (!carPanel || !carGrid) return;
      const query = carSearch ? carSearch.value : "";
      const hasQuery = String(query || "").trim().length > 0;

      if (!activeSegment && !hasQuery) {
        carPanel.hidden = true;
        carGrid.innerHTML = "";
        return;
      }

      const sourceCars = hasQuery ? data.cars : activeSegment ? getCarsForSegment(activeSegment) : data.cars;
      const cars = filterCars(sourceCars, query);
      carPanel.hidden = false;
      if (!cars.length) {
        carGrid.innerHTML = `<p class="stitch-copy-muted stitch-car-grid__empty">No matching vehicles found. Try another make or model.</p>`;
        return;
      }

      carGrid.innerHTML = cars.map((car) => renderCarOption(car, car.slug === activeCar)).join("");
    }

    function renderResults() {
      const car = data.cars.find((candidate) => candidate.slug === activeCar);

      if (!car) {
        syncSelectedCarState(null);
        results.hidden = true;
        status.hidden = false;
        if (selectedVehicle) selectedVehicle.innerHTML = "";
        if (resultsContext) {
          resultsContext.textContent = "";
          resultsContext.style.display = "none";
        }
        if (bundleCta) bundleCta.hidden = true;
        const hasModelPicked = modelSelect && modelSelect.value && !activeCar;
        status.querySelector(".stitch-copy-muted").textContent = hasModelPicked
          ? "Choose your car's year of manufacture to see recommendations."
          : "Choose your car to unlock the setup recommendations.";
        return;
      }

      syncSelectedCarState(car);
      const rankedProducts = getRankedProducts(car);
      lastRecommendations = buildRecommendations(activeGoal, rankedProducts, car);

      recommendationHeading.textContent = `Recommended Upgrade Paths for ${car.label}`;
      const activeGoalMeta = data.soundGoals.find((goal) => goal.id === activeGoal) || data.soundGoals[0];
      recommendationCopy.textContent = `Built around a ${car.segment === "suv_muv" ? "utility-style" : car.segment} cabin with a ${activeGoalMeta.label.toLowerCase()} bias.`;
      compatibleHeading.textContent = `Other Compatible Products for ${car.label}`;
      if (resultsContext && window._stitchSelectedCar) {
        resultsContext.textContent =
          "Showing recommendations for " +
          window._stitchSelectedCar +
          (window._stitchSelectedSegment ? " · " + window._stitchSelectedSegment : "");
        resultsContext.style.display = "inline-flex";
      }
      if (selectedVehicle) {
        selectedVehicle.innerHTML = `
          ${renderVehicleArtwork(car, "summary")}
          <div class="stitch-selected-vehicle__body">
            <span class="stitch-kicker">Selected Vehicle</span>
            <h2 class="stitch-title-md">${escapeHtml(car.label)}</h2>
            <p class="stitch-copy-muted">${escapeHtml(car.notes || "")}</p>
          </div>
        `;
      }

      recommendationGrid.innerHTML = lastRecommendations.map(renderRecommendationCard).join("");
      compatibleGrid.innerHTML = rankedProducts
        .map((product) => renderCompatibleCard(product, highlightedHandles.includes(product.handle)))
        .join("");
      selectedBundleVariantIds = [];
      if (bundleCta) bundleCta.hidden = true;

      status.hidden = true;
      results.hidden = false;
    }

    function renderBundleCallToAction(tier, items) {
      if (!bundleCta) return;
      const variantIds = (items || [])
        .map((item) => item && item.variantId)
        .filter(Boolean);

      if (!variantIds.length) {
        selectedBundleVariantIds = [];
        bundleCta.hidden = true;
        bundleCta.innerHTML = "";
        return;
      }

      selectedBundleVariantIds = Array.from(new Set(variantIds));
      bundleCta.hidden = false;
      bundleCta.innerHTML = `
        <button type="button" class="stitch-button" data-bundle-add>
          Add ${escapeHtml(tier)} Bundle to cart
        </button>
      `;
    }

    function highlightTier(tier) {
      const recommendation = lastRecommendations.find((item) => item.tier === tier);
      highlightedHandles = recommendation ? recommendation.items.map((item) => item.handle) : [];

      root.querySelectorAll("[data-compatible-handle]").forEach((card) => {
        card.classList.toggle("is-highlighted", highlightedHandles.includes(card.dataset.compatibleHandle));
      });
      renderBundleCallToAction(tier, recommendation ? recommendation.items : []);

      const target = document.getElementById("vehicle-compatible-products");
      if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    if (segmentSelector) {
      segmentSelector.addEventListener("click", (event) => {
        const button = event.target.closest("[data-segment-option]");
        if (!button) return;

        activeSegment = button.dataset.segmentOption;
        activeCar = "";
        highlightedHandles = [];
        trackEvent("vehicle_selector_segment", { segment: activeSegment });
        updateSegmentUI();
        renderCars();
        renderResults();
      });
    }

    goalSelector.addEventListener("click", (event) => {
      const button = event.target.closest("[data-goal-option]");
      if (!button) return;

      activeGoal = button.dataset.goalOption;
      highlightedHandles = [];
      trackEvent("vehicle_selector_goal", { goal: activeGoal });
      updateGoalUI();
      renderResults();
    });

    if (carGrid) {
      carGrid.addEventListener("click", (event) => {
        const button = event.target.closest("[data-car-option]");
        if (!button) return;

        activeCar = button.dataset.carOption;
        const selectedCar = data.cars.find((candidate) => candidate.slug === activeCar);
        if (selectedCar) {
          activeSegment = selectedCar.segment;
          syncSelectedCarState(selectedCar);
          trackEvent("vehicle_selector_car", {
            car: selectedCar.slug,
            segment: selectedCar.segment,
          });
        }
        highlightedHandles = [];
        updateSegmentUI();
        renderCars();
        renderResults();
      });
    }

    if (carSearch) {
      carSearch.addEventListener("keydown", (event) => {
        if (event.key !== "Enter") return;
        event.preventDefault();
        renderCars();
      });
      carSearch.addEventListener("input", () => {
        renderCars();
      });
    }

    if (carSearchSubmit) {
      carSearchSubmit.addEventListener("click", () => {
        renderCars();
      });
    }

    recommendationGrid.addEventListener("click", (event) => {
      const button = event.target.closest("[data-view-setup]");
      if (!button) return;
      trackEvent("vehicle_selector_tier_view", {
        tier: button.dataset.viewSetup,
        goal: activeGoal,
      });
      highlightTier(button.dataset.viewSetup);
    });

    if (bundleCta) {
      bundleCta.addEventListener("click", async (event) => {
        const button = event.target.closest("[data-bundle-add]");
        if (!button || !selectedBundleVariantIds.length) return;

        event.preventDefault();
        animateAddedState(button, "Added");
        adjustCartCountBadge(selectedBundleVariantIds.length);
        trackEvent("vehicle_selector_bundle_button_click", {
          bundle_size: selectedBundleVariantIds.length,
          goal: activeGoal,
          car: activeCar,
        });
        addVariantsBundle(selectedBundleVariantIds).catch(() => {
          refreshCart(true).catch(() => {});
        });
      });
    }

    updateSegmentUI();
    updateGoalUI();
    initMakeModelSelects();

    if (activeSegment) renderCars();
    if (!activeSegment && activeCar) {
      const car = data.cars.find((candidate) => candidate.slug === activeCar);
      if (car) activeSegment = car.segment;
    }

    updateSegmentUI();
    renderCars();
    renderResults();

  }

  function initMobileStickyAtc() {
    var mainAtc = document.querySelector('.stitch-product-panel .stitch-button[type="submit"]');
    var mobileSticky = document.getElementById('stitch-pdp-mobile-sticky');
    if (mainAtc && mobileSticky) {
      var obs = new IntersectionObserver(function (entries) {
        mobileSticky.classList.toggle('is-visible', !entries[0].isIntersecting);
      }, { threshold: 0 });
      obs.observe(mainAtc);
    }
  }

  function initProductRecommendations() {
    var container = document.getElementById("product-recommendations");
    if (!container) return;
    var url = container.getAttribute("data-url");
    if (!url) return;
    fetch(url)
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, "text/html");
        var inner = doc.getElementById("product-recommendations");
        if (inner && inner.innerHTML.trim()) {
          container.innerHTML = inner.innerHTML;
          container.style.display = "";
          initCardNavigationAndCart(container);
        }
      })
      .catch(function () {});
  }

  function initRecentlyViewed() {
    var storageKey = "zessqaa_recently_viewed";
    var container = document.getElementById("stitch-recently-viewed");
    var recContainer = document.getElementById("product-recommendations");
    if (!recContainer) return;

    var productId = recContainer.getAttribute("data-product-id");
    if (!productId) return;

    var titleEl = document.querySelector(".stitch-product-panel h1");
    var priceEl = document.querySelector(".stitch-product-panel .stitch-price");
    var imgEl = document.querySelector(".stitch-product-gallery__hero img");

    var stored = [];
    try { stored = JSON.parse(localStorage.getItem(storageKey) || "[]"); } catch (e) {}

    var current = {
      id: productId,
      title: titleEl ? titleEl.textContent.trim() : "",
      price: priceEl ? priceEl.textContent.trim() : "",
      image: imgEl ? imgEl.src : "",
      url: window.location.pathname
    };
    stored = stored.filter(function (p) { return p.id !== current.id; });
    stored.unshift(current);
    stored = stored.slice(0, 6);
    try { localStorage.setItem(storageKey, JSON.stringify(stored)); } catch (e) {}

    if (!container) return;
    var others = stored.filter(function (p) { return p.id !== productId; });
    if (!others.length) return;

    var html = '<div class="stitch-shell-wide" style="padding:48px 0 16px;">'
      + '<span class="stitch-kicker">Recently viewed</span>'
      + '<div class="stitch-products-grid" style="margin-top:16px;">';
    others.slice(0, 4).forEach(function (p) {
      html += '<a href="' + escapeHtml(p.url) + '" style="text-decoration:none;" class="stitch-product-card">'
        + (p.image ? '<div class="stitch-product-card__media"><img src="' + escapeHtml(p.image) + '" alt="' + escapeHtml(p.title) + '" loading="lazy" style="width:100%;height:100%;object-fit:cover;border-radius:4px;"></div>' : "")
        + '<div class="stitch-product-card__meta">'
        + '<span class="stitch-kicker">Car Audio</span>'
        + '<h3 class="stitch-title-lg" style="font-size:1rem;">' + escapeHtml(p.title) + "</h3>"
        + '<span class="stitch-price">' + escapeHtml(p.price) + "</span>"
        + "</div></a>";
    });
    html += "</div></div>";
    container.innerHTML = html;
    container.style.display = "block";
  }

  function injectAtcDrawerStyles() {
    if (document.getElementById('stitch-atc-drawer-styles')) return;
    var style = document.createElement('style');
    style.id = 'stitch-atc-drawer-styles';
    style.textContent = [
      '#stitch-atc-overlay{position:fixed;inset:0;background:rgba(0,0,0,.55);z-index:9998;opacity:0;transition:opacity .3s;pointer-events:none;}',
      '#stitch-atc-overlay.is-open{opacity:1;pointer-events:auto;}',
      '#stitch-atc-drawer{position:fixed;top:0;right:0;height:100%;width:380px;max-width:100vw;background:#1a1a2e;border-left:1px solid #c9a84c;z-index:9999;transform:translateX(100%);transition:transform .32s cubic-bezier(.4,0,.2,1);display:flex;flex-direction:column;box-shadow:-4px 0 32px rgba(0,0,0,.6);}',
      '#stitch-atc-drawer.is-open{transform:translateX(0);}',
      '#stitch-atc-drawer__header{display:flex;align-items:center;justify-content:space-between;padding:20px 20px 16px;border-bottom:1px solid rgba(201,168,76,.25);}',
      '#stitch-atc-drawer__header h2{margin:0;font-size:0.95rem;font-weight:700;color:#c9a84c;letter-spacing:.06em;text-transform:uppercase;}',
      '#stitch-atc-drawer__close{background:none;border:none;cursor:pointer;color:#e8e3d9;padding:4px;line-height:1;font-size:1.25rem;opacity:.75;transition:opacity .2s;}',
      '#stitch-atc-drawer__close:hover{opacity:1;}',
      '#stitch-atc-drawer__body{flex:1;overflow-y:auto;padding:20px;}',
      '#stitch-atc-drawer__item{display:flex;gap:14px;align-items:flex-start;padding-bottom:16px;border-bottom:1px solid rgba(201,168,76,.2);margin-bottom:16px;}',
      '#stitch-atc-drawer__item-img{width:72px;height:72px;border-radius:6px;object-fit:cover;flex-shrink:0;background:rgba(255,255,255,.05);border:1px solid rgba(201,168,76,.2);}',
      '#stitch-atc-drawer__item-info{flex:1;min-width:0;}',
      '#stitch-atc-drawer__item-title{font-size:0.9rem;font-weight:600;color:#e8e3d9;margin:0 0 4px;line-height:1.3;}',
      '#stitch-atc-drawer__item-variant{font-size:0.78rem;color:rgba(232,227,217,.55);margin:0 0 6px;}',
      '#stitch-atc-drawer__item-meta{display:flex;align-items:center;gap:12px;}',
      '#stitch-atc-drawer__item-price{font-size:0.92rem;font-weight:700;color:#c9a84c;}',
      '#stitch-atc-drawer__item-qty{font-size:0.78rem;color:rgba(232,227,217,.55);}',
      '#stitch-atc-drawer__total{display:flex;justify-content:space-between;align-items:center;padding:4px 0 16px;}',
      '#stitch-atc-drawer__total-label{font-size:0.82rem;color:rgba(232,227,217,.65);text-transform:uppercase;letter-spacing:.05em;}',
      '#stitch-atc-drawer__total-value{font-size:1rem;font-weight:700;color:#e8e3d9;}',
      '#stitch-atc-drawer__footer{padding:16px 20px 20px;border-top:1px solid rgba(201,168,76,.2);display:flex;flex-direction:column;gap:10px;}',
      '#stitch-atc-drawer__continue{background:none;border:1px solid rgba(232,227,217,.3);color:#e8e3d9;padding:13px 20px;border-radius:6px;font-size:0.88rem;font-weight:600;cursor:pointer;transition:border-color .2s,color .2s;text-align:center;}',
      '#stitch-atc-drawer__continue:hover{border-color:#c9a84c;color:#c9a84c;}',
      '#stitch-atc-drawer__checkout{background:#c9a84c;border:none;color:#1a1a2e;padding:14px 20px;border-radius:6px;font-size:0.92rem;font-weight:700;cursor:pointer;text-align:center;text-decoration:none;display:block;letter-spacing:.04em;transition:background .2s;}',
      '#stitch-atc-drawer__checkout:hover{background:#d4b55e;}',
      '@media(max-width:480px){#stitch-atc-drawer{width:100vw;border-left:none;border-top:1px solid #c9a84c;top:auto;bottom:0;height:90vh;transform:translateY(100%);}#stitch-atc-drawer.is-open{transform:translateY(0);}}'
    ].join('');
    document.head.appendChild(style);
  }

  function closeAtcDrawer() {
    var drawer = document.getElementById('stitch-atc-drawer');
    var overlay = document.getElementById('stitch-atc-overlay');
    if (drawer) drawer.classList.remove('is-open');
    if (overlay) overlay.classList.remove('is-open');
    document.removeEventListener('keydown', closeAtcDrawerOnEscape);
  }

  function closeAtcDrawerOnEscape(e) {
    if (e.key === 'Escape') closeAtcDrawer();
  }

  function formatAtcPrice(cents) {
    if (typeof cents !== 'number' || isNaN(cents)) return '';
    return '₹' + (cents / 100).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }

  function showAtcDrawer(cart, quantityAdded) {
    injectAtcDrawerStyles();

    var overlay = document.getElementById('stitch-atc-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'stitch-atc-overlay';
      document.body.appendChild(overlay);
    }
    overlay.addEventListener('click', closeAtcDrawer);

    var drawer = document.getElementById('stitch-atc-drawer');
    if (!drawer) {
      drawer = document.createElement('div');
      drawer.id = 'stitch-atc-drawer';
      drawer.setAttribute('role', 'dialog');
      drawer.setAttribute('aria-modal', 'true');
      drawer.setAttribute('aria-label', 'Added to cart');
      document.body.appendChild(drawer);
    }

    var lastItem = null;
    if (cart && cart.items && cart.items.length) {
      lastItem = cart.items[0];
    }

    var imgHtml = lastItem && lastItem.image
      ? '<img id="stitch-atc-drawer__item-img" src="' + escapeHtml(lastItem.image) + '" alt="' + escapeHtml(lastItem.product_title || '') + '">'
      : '<div id="stitch-atc-drawer__item-img"></div>';

    var titleHtml = lastItem ? escapeHtml(lastItem.product_title || lastItem.title || '') : '';
    var variantHtml = (lastItem && lastItem.variant_title && lastItem.variant_title !== 'Default Title')
      ? '<p id="stitch-atc-drawer__item-variant">' + escapeHtml(lastItem.variant_title) + '</p>'
      : '';
    var priceHtml = lastItem ? formatAtcPrice(lastItem.price) : '';
    var qtyLabel = 'Qty: ' + (quantityAdded || 1);
    var totalHtml = cart ? formatAtcPrice(cart.total_price) : '';

    drawer.innerHTML = '<div id="stitch-atc-drawer__header">'
      + '<h2>Added to Cart</h2>'
      + '<button id="stitch-atc-drawer__close" aria-label="Close cart drawer">&times;</button>'
      + '</div>'
      + '<div id="stitch-atc-drawer__body">'
      + '<div id="stitch-atc-drawer__item">'
      + imgHtml
      + '<div id="stitch-atc-drawer__item-info">'
      + '<p id="stitch-atc-drawer__item-title">' + titleHtml + '</p>'
      + variantHtml
      + '<div id="stitch-atc-drawer__item-meta">'
      + '<span id="stitch-atc-drawer__item-price">' + priceHtml + '</span>'
      + '<span id="stitch-atc-drawer__item-qty">' + qtyLabel + '</span>'
      + '</div>'
      + '</div>'
      + '</div>'
      + '<div id="stitch-atc-drawer__total">'
      + '<span id="stitch-atc-drawer__total-label">Cart Total</span>'
      + '<span id="stitch-atc-drawer__total-value">' + totalHtml + '</span>'
      + '</div>'
      + '</div>'
      + '<div id="stitch-atc-drawer__footer">'
      + '<button id="stitch-atc-drawer__continue">Continue Shopping</button>'
      + '<a href="/discount/JUNE15?redirect=/checkout" id="stitch-atc-drawer__checkout">Proceed to Checkout</a>'
      + '</div>';

    drawer.querySelector('#stitch-atc-drawer__close').addEventListener('click', closeAtcDrawer);
    drawer.querySelector('#stitch-atc-drawer__continue').addEventListener('click', closeAtcDrawer);

    requestAnimationFrame(function() {
      overlay.classList.add('is-open');
      drawer.classList.add('is-open');
    });

    document.addEventListener('keydown', closeAtcDrawerOnEscape);
  }

  function initPdpAjaxAtc() {
    function handleAtcSubmit(form, event) {
      event.preventDefault();
      var variantInput = form.querySelector('input[name="id"]');
      var quantityInput = form.querySelector('input[name="quantity"]');
      var submitButton = form.querySelector('[type="submit"]:not([disabled])');
      var variantId = variantInput ? Number(variantInput.value) : 0;
      var rawQty = quantityInput ? String(quantityInput.value).trim() : '1';
      var quantity = Number(rawQty);
      if (!variantId || !Number.isInteger(quantity) || quantity < 1) return;
      if (submitButton) animateAddedState(submitButton, 'Added');
      adjustCartCountBadge(quantity);
      trackEvent('pdp_atc_submit', { variant_id: variantId, quantity: quantity });
      addVariantToCart(variantId, quantity)
        .then(function(cart) { showAtcDrawer(cart, quantity); })
        .catch(function() {
          refreshCart(true).catch(function() {});
        });
    }

    var mainForm = document.querySelector('[data-pdp-form]');
    if (mainForm) {
      mainForm.addEventListener('submit', function(e) { handleAtcSubmit(mainForm, e); });
    }

    var mobileSticky = document.querySelector('.stitch-pdp-mobile-sticky');
    var mobileForm = mobileSticky && mobileSticky.querySelector('form');
    if (mobileForm) {
      mobileForm.addEventListener('submit', function(e) { handleAtcSubmit(mobileForm, e); });
    }

    var stickyForm = document.querySelector('.stitch-pdp-sticky__form');
    if (stickyForm) {
      stickyForm.addEventListener('submit', function(e) { handleAtcSubmit(stickyForm, e); });
    }
  }

  function initCouponApplyAddToCart() {
    // Intercept PDP coupon "Tap to apply" links so the product is added to cart
    // before the browser follows the /discount/CODE?redirect=/cart URL.
    // Without this, the shopper lands on an empty cart — the discount applies
    // but there is nothing in the cart to discount.
    document.addEventListener('click', function(event) {
      var anchor = event.target.closest('a.stitch-pdp-coupon-badge, a.stitch-customer-coupon-nudge');
      if (!anchor) return;

      // Only intercept on product pages — guard with presence of [data-pdp-form].
      var form = document.querySelector('[data-pdp-form]');
      if (!form) return; // not a PDP — let the link behave normally

      // Prevent double-bind / in-flight re-entry
      if (anchor.dataset.couponInFlight === '1') {
        event.preventDefault();
        return;
      }

      // Resolve selected variant — prefer :checked (multi-variant), fallback to first hidden id
      var variantInput = form.querySelector('input[name="id"]:checked') || form.querySelector('input[name="id"]');
      var variantId = variantInput ? Number(variantInput.value) : 0;
      if (!variantId) return; // no valid variant — let the link work as before

      var qtyInput = form.querySelector('input[name="quantity"]');
      var quantity = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;

      var href = anchor.href; // capture before preventDefault

      event.preventDefault();

      // Light feedback — swap CTA text
      var ctaSpan = anchor.querySelector('.stitch-pdp-coupon-badge__cta, .stitch-customer-coupon-nudge__cta');
      var originalCta = ctaSpan ? ctaSpan.textContent : null;
      if (ctaSpan) ctaSpan.textContent = 'Adding…';
      anchor.dataset.couponInFlight = '1';

      addVariantToCart(variantId, quantity)
        .catch(function() { /* silent — proceed anyway; discount still applies */ })
        .finally(function() {
          anchor.dataset.couponInFlight = '0';
          if (ctaSpan && originalCta) ctaSpan.textContent = originalCta;
          window.location.href = href;
        });
    });
  }

  document.addEventListener("DOMContentLoaded", function () {
    initMobileNavDrawer();
    initInstantSearchOverlay();
    initPdpStickyBar();
    initPdpGalleryAndVariants();
    initPdpFormValidation();
    initPdpAjaxAtc();
    initCouponApplyAddToCart();
    initRevealUp();
    initCardNavigationAndCart(document);
    initMobileStickyAtc();
    refreshCart(true).catch(() => {});
    document.querySelectorAll("[data-zessqaa-vehicle-match]").forEach(initVehicleMatch);
    initProductRecommendations();
    initRecentlyViewed();
  });
})();
